import React, { useEffect, useMemo, useState } from "react";
import HeaderPanel from "../layout/HeaderPanel";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import FooterPanel from "../layout/FooterPanel";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { Dropdown, IComboBoxOption, IconButton, IContextualMenuProps, Stack, TeachingBubble } from "@fluentui/react";
import {
  processTreeViewItems,
  TreeViewCheckChangeEvent,
  TreeViewExpandChangeEvent,
} from "@progress/kendo-react-treeview";
import { mapFormToDataItem, TreeViewDataItem } from "../cpm/DocumentPartTree";
import {
  CallbackMappingMessage,
  fetchDbMapping,
  fetchDocumentPartNodeWithPermissions,
  fetchDocumentsDbList,
  fetchManageDocumentPartOutline,
} from "../../api/document/DocumentsPart";
import { DocumentRoot } from "../../api/models/document-root";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import { DocumentPartIdNamePair } from "../../api/models/document-part-id-name-pair";
import { fetchGroupUserDB } from "../../api/object/Form";
import { DocumentDbMapping } from "../../api/models/document-db-mapping";
import NotificationManager from "../../common/notification/NotificationManager";
import { ElaborationMessage } from "../../redux/feature/object/remote-elaborations-slice";
import { Checkbox } from "@progress/kendo-react-inputs";
import { CustomTreeView } from "../cpm/CustomTreeView";

interface DBMappingProps {
  expandIcons?: boolean;
  getIconButtonMenuProps?: (item: any, itemHierarchicalIndex: string) => IContextualMenuProps;
  selectedTreeItem: FlatPathTreeItem;
  onBackButtonClick: () => void;
}

export interface DBMappingState {
  selectState: string[];
  selectedId?: string;
  expand: string[];
  check: Map<string, string>;
  teachingBubbleVisible: boolean;
  documentDbList: Map<string, DocumentPartIdNamePair[]>;
  manageTreeItems: TreeViewDataItem[];
  page: "selectItem" | "selectDb";
  dbApplicationItems: IComboBoxOption[];
  activeDb: string;
  mapDb: Map<string, DocumentDbMapping[]>;
  maxSelectable: number;
}

export const getFullTree = async (rootId: string): Promise<TreeViewDataItem[]> => {
  const structureDocumentParts = await fetchManageDocumentPartOutline(rootId);
  return [mapStructureDocumentPartToTreeViewDataItem(structureDocumentParts, 0)];
};

const mapStructureDocumentPartToTreeViewDataItem = (structureDocumentPart, index): TreeViewDataItem => {
  return {
    text: structureDocumentPart.name,
    key: structureDocumentPart.id,
    order: index,
    hasChildren: null,
    driveItemId: structureDocumentPart.driveItemId,
    nodeType: "part",
    expanded: true,
    checked: false,
    items: structureDocumentPart.subparts?.map(mapStructureDocumentPartToTreeViewDataItem),
    data: structureDocumentPart,
  };
};

export const DBMapping: React.FunctionComponent<DBMappingProps> = (props) => {
  const expandIcons: boolean = props.expandIcons == undefined;
  const { selectedTreeItem } = props;
  const [state, setState] = useState<DBMappingState>({
    activeDb: "",
    documentDbList: new Map<string, DocumentPartIdNamePair[]>(),
    manageTreeItems: [],
    selectState: [],
    expand: [],
    check: new Map<string, string>(),
    page: "selectItem",
    dbApplicationItems: [],
    teachingBubbleVisible: false,
    mapDb: new Map(),
    maxSelectable: 0,
  });

  const rootItem = useMemo<TreeViewDataItem[]>(() => {
    return [
      {
        text: selectedTreeItem.document.name,
        key: selectedTreeItem.document.id,
        order: selectedTreeItem.document.order,
        hasChildren: selectedTreeItem.document.hasChildren,
        nodeType: "part",
        expanded: false,
        items: undefined,
        data: selectedTreeItem.document,
        hasReadPermissions: selectedTreeItem.readPermission,
        checked: false,
      },
    ];
  }, [selectedTreeItem]);

  useEffect(() => {
    getFullTree(selectedTreeItem.document.id).then((treeItems) => {
      if (!treeItems?.length) {
        return;
      }

      const countSelectableItems = (items: TreeViewDataItem[]) =>
        items.reduce((count, item) => {
          let childCount = item.items ? countSelectableItems(item.items) : 0;
          return count + 1 + childCount;
        }, 0);

      setState((s) => ({ ...s, manageTreeItems: treeItems, maxSelectable: countSelectableItems(treeItems) }));
    });
  }, []);

  const onExpandChangeManageTree = (event: TreeViewExpandChangeEvent) => {
    fetchExpandedNodeManageTree(event.itemHierarchicalIndex, false);
  };

  const fetchExpandedNode = (id: string, isReload: boolean, page: number = 0) => {
    if (!id || id.length === 0) return;
    const indexes = id.split("_").map((value) => Number(value)) ?? [];
    if (page !== 0) {
      indexes.pop();
    }
    const { items, item } = copyItemsAndItem(rootItem, indexes);
    item.expanded = isReload ? true : !item.expanded;
    if (!isReload && item.items && page === 0) {
      setState((s) => ({ ...s, treeItems: items }));
      return;
    }
    fetchDocumentPartNodeWithPermissions(item.key, page).then((dataPart) => {
      let childrenDocumentPart: DocumentRoot[] = [];
      dataPart._embedded.content.forEach((part) => childrenDocumentPart.push(part));
      const { items: children, item: parentItem } = copyItemsAndItem(items, indexes);
      if (page !== 0) {
        parentItem.expanded = true;
        let childrens = parentItem.data.subparts as DocumentRoot[];
        parentItem.data.subparts = childrens.concat(childrenDocumentPart);
        parentItem.items.pop();
        parentItem.items = parentItem.items.concat(childrenDocumentPart.map((part) => mapFormToDataItem(part)));
      } else {
        parentItem.data.subparts = childrenDocumentPart;
        parentItem.items = childrenDocumentPart.map((part) => mapFormToDataItem(part));
      }

      if (dataPart.page.totalPages != page + 1) {
        parentItem.items.push({
          text: formatMessage("cof.documentslist.showmore"),
          key: "key",
          order: 1,
          hasChildren: false,
          nodeType: "pagination",
          data: { ...dataPart.page, idParent: item.key },
        });
      }
      parentItem.items.sort((a, b) => a.order - b.order);
      parentItem.hasChildren = false; // hasChildren is need only for lazy loading
      setState((s) => ({ ...s, treeItems: children }));
    });
  };

  const fetchExpandedNodeManageTree = (id: string, isReload: boolean) => {
    if (id && id.length > 0) {
      const indexes = id.split("_").map((value) => Number(value)) ?? [];
      const { items, item } = copyItemsAndItem(state.manageTreeItems, indexes);
      item.expanded = isReload ? true : !item.expanded;
      setState((s) => ({ ...s, manageTreeItems: items }));
    }
  };
  const fetchCheckedNode = (event: TreeViewCheckChangeEvent) => {
    if (event.itemHierarchicalIndex && event.itemHierarchicalIndex.length > 0) {
      const indexes = event.itemHierarchicalIndex.split("_").map((value) => Number(value)) ?? [];
      const { items, item } = copyItemsAndItem(state.manageTreeItems, indexes);
      item.checked = !item.checked;
      setState((s) => {
        if (item.checked) {
          // Se l'elemento è stato appena controllato, aggiungilo all'array
          s.check.set(event.item.key, event.item.text);
        } else {
          // Se l'elemento è stato appena deselezionato, rimuovilo dall'array
          s.check.delete(event.item.key);
        }
        return { ...s, manageTreeItems: items };
      });
    }
  };

  const toggleSelectAll = (isChecked: boolean) => {
    setState((s) => {
      const updatedItems = s.manageTreeItems.map((item) => checkAllItems(item, isChecked));

      const updatedCheck = new Map();

      if (isChecked) {
        populateCheckedMap(updatedItems, updatedCheck);
      }

      return {
        ...s,
        manageTreeItems: updatedItems,
        check: updatedCheck,
      };
    });
  };

  const checkAllItems = (item, isChecked) => {
    const newItem = { ...item, checked: isChecked };
    if (newItem.items) {
      newItem.items = newItem.items.map((child) => checkAllItems(child, isChecked));
    }
    return newItem;
  };

  const populateCheckedMap = (items, checkMap) => {
    items.forEach((item) => {
      if (item.checked && item.key) {
        checkMap.set(item.key, item.text);
      }
      if (item.items) {
        populateCheckedMap(item.items, checkMap);
      }
    });
  };

  const onCheckChange = (event: TreeViewCheckChangeEvent) => {
    fetchCheckedNode(event);
  };

  const handleDBMapping = () => {
    const entries = Object.fromEntries(state.mapDb);
    fetchDbMapping(entries).then((callbackMapping: CallbackMappingMessage) => {
      const warnings: ElaborationMessage = {
        details:
          callbackMapping.details.length > 1 ? callbackMapping.details.join(",\n") : callbackMapping.details.join(),
        severity: "WARNING",
        traceId: "NOT DEFINED",
        text: formatMessage("cof.base.opendocument"),
      };
      NotificationManager.addNotification({
        title:
          callbackMapping.status != "COMPLETED"
            ? formatMessage("cof.opendocuments.outline.dbmapping.title.warning")
            : formatMessage("cof.opendocuments.outline.dbmapping.title"),
        message:
          callbackMapping.status != "COMPLETED" ? null : formatMessage("cof.opendocuments.outline.dbmapping.message"),
        type: callbackMapping.status != "COMPLETED" ? "warning" : "success",
        closable: true,
        removeOnClose: true,
        isTemporary: callbackMapping.status == "COMPLETED",
        details: callbackMapping.status != "COMPLETED" ? [warnings] : null,
      });
      props.onBackButtonClick();
    });
  };

  const handleDocumentDBList = () => {
    fetchDocumentsDbList(Object.fromEntries(state.check)).then((documentDB) => {
      fetchGroupUserDB().then((dbs) => {
        const dbApplicationMap: IComboBoxOption[] = dbs.map((db) => {
          return {
            key: db.id,
            text: db.desc,
          };
        });
        setState({
          ...state,
          page: "selectDb",
          dbApplicationItems: dbApplicationMap,
          documentDbList: documentDB,
        });
      });
    });
  };

  const copyItemsAndItem = (
    items: TreeViewDataItem[],
    indexes: number[]
  ): { items: TreeViewDataItem[]; item: TreeViewDataItem } => {
    const copiedItems = items.slice();
    const item = (copiedItems[indexes[0]] = { ...copiedItems[indexes[0]] });

    if (indexes.length > 1) {
      const { items: children, item: target } = copyItemsAndItem(item.items, indexes.slice(1));
      item.items = children;
      return { items: copiedItems, item: target };
    } else {
      return { items: copiedItems, item: item };
    }
  };

  const handleDropdownChange = (oldDb: string, newDb: string | number) => {
    const oldMap = state.mapDb;
    const db = { oldDbId: oldDb, newDbId: newDb };
    state.documentDbList[oldDb].forEach((item) => {
      if (oldMap.has(item.documentId)) {
        // Se la chiave esiste già, aggiungi il nuovo valore alla lista esistente
        const existingValue = oldMap.get(item.documentId);
        existingValue.push(db);
        oldMap.set(item.documentId, existingValue);
      } else {
        // Se la chiave non esiste, crea una nuova lista con il nuovo valore
        oldMap.set(item.documentId, [db]);
      }
    });
    setState((s) => ({ ...s, mapDb: oldMap }));
  };

  return (
    <>
      <HeaderPanel data-testid={"header"} applyPadding>
        {formatMessage("cof.opendocuments.outline.dbmapping") + " - " + props.selectedTreeItem.text}
      </HeaderPanel>
      {state.page === "selectItem" && (
        <div
          style={{
            padding: "12px 8px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--color-grey)",
              fontSize: "14px",
            }}
          >
            {formatMessage("cof.opendocuments.outline.dbmapping.info")}
          </div>
          <div
            style={{
              borderBottom: `0.01rem solid ${"var(--color-grey)"}`,
              width: "100%",
              height: "3px",
            }}
          />
          <Stack horizontal>
            <Checkbox
              className="k-checkbox-wrap"
              checked={state.check.size === state.maxSelectable}
              onChange={(e) => toggleSelectAll(e.value)}
              value={state.check.size != 0 && state.check.size < state.maxSelectable ? null : true}
            />
            <label style={{ fontWeight: "bold", color: "var(--color-blue)", fontSize: "0.9rem", marginLeft: "0.5rem" }}>
              {formatMessage("cof.objectlist.selectall")}
            </label>
          </Stack>
          <CustomTreeView
            data={processTreeViewItems(state.manageTreeItems, {
              expand: state.expand,
              select: state.selectState,
            })}
            expandIcons={expandIcons}
            onExpandChange={onExpandChangeManageTree}
            size={"small"}
            checkboxes={true}
            onCheckChange={onCheckChange}
            selectedItemId={props.selectedTreeItem.document.id}
            treeItemRowProps={{
              getIconButtonMenuProps: props.getIconButtonMenuProps,
              fetchExpandedNode,
            }}
          />
        </div>
      )}
      {state.page === "selectDb" && (
        <div
          style={{
            padding: "12px 8px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--color-grey)",
              fontSize: "14px",
            }}
          >
            {formatMessage("cof.opendocuments.outline.dbmapping.info.db")}
          </div>
          <div
            style={{
              borderBottom: `0.01rem solid ${"var(--color-grey)"}`,
              width: "100%",
              height: "3px",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", padding: "12px 0px" }}>
            <>
              <div style={{ fontSize: "0.825rem", fontWeight: "bold", color: "var(--color-black)" }}>
                {formatMessage("cof.opendocuments.outline.dbmapping.info.sourcedb")}
              </div>
              <div
                style={{ fontSize: "0.825rem", fontWeight: "bold", color: "var(--color-black)", paddingRight: "2rem" }}
              >
                {formatMessage("cof.opendocuments.outline.dbmapping.info.destinationdb")}
              </div>
              <div
                style={{
                  borderBottom: `0.01rem solid ${"var(--color-blue)"}`,
                  width: "100%",
                  height: "3px",
                }}
              />
            </>
          </div>
          {Array.from(Object.keys(state.documentDbList)).map((key) => (
            <div className={`card`} key={key}>
              <div style={{ fontSize: "1rem", color: "var(--color-blue)", fontWeight: "bold" }}>
                {state.documentDbList[key].length === 1
                  ? state.documentDbList[key][0].documentName
                  : state.documentDbList[key].length + " Documents"}
                {state.documentDbList[key].length > 1 && (
                  <IconButton
                    id={`infoId_${key}`}
                    aria-label="Info"
                    iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
                    onClick={() =>
                      setState({ ...state, teachingBubbleVisible: !state.teachingBubbleVisible, activeDb: key })
                    }
                  />
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", padding: "12px 0px" }}>
                <div style={{ fontSize: "0.75rem" }}>{key}</div>
                <Dropdown
                  options={state.dbApplicationItems}
                  placeholder={formatMessage("cof.base.selectdb")}
                  style={{ fontSize: "0.75rem" }}
                  onChange={(_event, item) => handleDropdownChange(key, item.key)}
                />
              </div>
            </div>
          ))}
          {state.teachingBubbleVisible && (
            <TeachingBubble
              calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
              target={`#infoId_${state.activeDb}`}
              hasCloseButton={true}
              closeButtonAriaLabel="Close"
              onDismiss={() => setState({ ...state, teachingBubbleVisible: false })}
            >
              {state.documentDbList[state.activeDb].map((item, index) => (
                <p key={index}>{item.documentName}</p>
              ))}
            </TeachingBubble>
          )}
        </div>
      )}
      <FooterPanel>
        {state.page === "selectItem" && (
          <>
            <PrimaryButton
              id="next_button"
              className={ButtonVariants.primary}
              disabled={state.check?.size === 0}
              data-testid={"next"}
              text={formatMessage("cof.base.next")}
              style={{ float: "right", marginLeft: "15px" }}
              onClick={handleDocumentDBList}
            />
            <PrimaryButton
              id="back_button"
              className={ButtonVariants.primary}
              data-testid={"back"}
              text={formatMessage("cof.base.back")}
              onClick={props.onBackButtonClick}
              style={{
                float: "left",
              }}
            />
          </>
        )}
        {state.page === "selectDb" && (
          <>
            <PrimaryButton
              id="back_button"
              className={ButtonVariants.white}
              data-testid={"cancel"}
              text={formatMessage("cof.base.back")}
              style={{ float: "left", marginLeft: "15px" }}
              onClick={() => {
                setState((s) => ({
                  ...s,
                  page: "selectItem",
                  mapDb: new Map(),
                }));
              }}
            />
            <PrimaryButton
              id="remap_button"
              className={ButtonVariants.primary}
              data-testid={"next"}
              disabled={state.mapDb?.size === 0}
              text={formatMessage("cof.opendocuments.outline.dbmapping.remap")}
              style={{ float: "right", marginLeft: "15px" }}
              onClick={handleDBMapping}
            />
          </>
        )}
      </FooterPanel>
    </>
  );
};
